import { useEffect, useRef } from "react";
import {
  listarEventosCalendario,
  getLembreteAtivo,
  getLembreteAntecedencia,
  EVENTO_VISUAL,
} from "../services/calendario";
import { useLembretesCalendarioStore } from "../store/lembretesCalendarioStore";

/**
 * Verifica eventos próximos do calendário e dispara notificações do navegador
 * X minutos antes (configurável). Eventos sem horário (dia inteiro) usam 08:00 como alvo.
 *
 * Estado:
 * - Tick a cada 60s; também dispara imediatamente ao montar.
 * - Mantém um Set em memória com eventos já notificados pra não repetir na mesma sessão.
 * - Respeita o toggle on/off e a antecedência configurada.
 * - Requer Notification.permission === "granted". Se não tiver, é silencioso.
 */
export function useCalendarioLembretes() {
  const notificados = useRef<Set<string>>(new Set());
  const addLembrete = useLembretesCalendarioStore((s) => s.add);

  useEffect(() => {
    let cancelado = false;

    async function tick() {
      if (cancelado) return;
      if (!getLembreteAtivo()) return;
      // Notification do browser é "nice to have" — sininho funciona mesmo sem permissão.
      const podeNotificar =
        typeof window !== "undefined" &&
        "Notification" in window &&
        Notification.permission === "granted";

      const antecedencia = getLembreteAntecedencia();
      const agora = new Date();
      // Cobre o dia atual e o próximo pra capturar eventos noturnos / madrugada.
      const ini = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
      const fim = new Date(agora.getTime() + 25 * 3600 * 1000);

      let eventos;
      try {
        eventos = await listarEventosCalendario(ini, fim);
      } catch {
        return;
      }
      if (cancelado) return;

      for (const ev of eventos) {
        // Calcula datetime alvo: data + hora (ou 08:00 se sem hora)
        const partes = ev.data.split("-").map(Number);
        if (partes.length !== 3) continue;
        const [y, m, d] = partes;
        const horaPartes = ev.hora ? ev.hora.split(":").map(Number) : [8, 0];
        const alvo = new Date(y, m - 1, d, horaPartes[0] ?? 8, horaPartes[1] ?? 0);

        const minutosFaltando = (alvo.getTime() - agora.getTime()) / 60000;
        // Janela: dentro da antecedência E ainda não chegou na hora.
        if (minutosFaltando < 0 || minutosFaltando > antecedencia) continue;

        // Chave única por evento+data — evita duplicar (recorrente anual gera múltiplas datas)
        const chave = `${ev.id || ev.titulo}-${ev.data}`;
        if (notificados.current.has(chave)) continue;
        notificados.current.add(chave);

        const visual = EVENTO_VISUAL[ev.tipo];
        const titulo = `${visual.emoji} ${visual.label}: ${ev.titulo}`;
        const corpo = ev.hora
          ? `Hoje às ${ev.hora}${ev.descricao ? ` — ${ev.descricao}` : ""}`
          : ev.descricao || "Evento de hoje";

        // 1) Notificação nativa do browser (se permitida)
        if (podeNotificar) {
          try { new Notification(titulo, { body: corpo, tag: `cal-${chave}` }); } catch {}
        }

        // 2) Sempre adiciona no sininho in-app (independe de permissão)
        addLembrete({
          id: chave,
          titulo,
          mensagem: corpo,
          tipo: ev.tipo,
          emoji: visual.emoji,
          link: "/calendario",
          criadoEm: new Date().toISOString(),
        });
      }
    }

    tick();
    const id = setInterval(tick, 60_000);
    return () => {
      cancelado = true;
      clearInterval(id);
    };
  }, []);
}
